<script lang="ts" setup>
  import { computed, ref } from "vue";
  import { useSessionStore } from "@/stores/session";
  import { useAppStore } from "@/stores/app";
  import { useSubscriptionFlowStore } from "@/stores/subscriptionFlow";
  import { DateTime } from "luxon";
  import { MembershipApiService, type UpdateMembershipPayload } from "@/services/membershipApiService";
  import useToast from "@/composables/toast";
  import { ToastType } from "@/models/app";
  import { useI18n } from "vue-i18n";
  import type { ApiError } from "@/utils/apiClient";
  import { MembershipTierPlanFrequency } from "@/models/membershipTierPlan";

  const appStore = useAppStore();
  const sessionStore = useSessionStore();
  const subscriptionFlowStore = useSubscriptionFlowStore();
  const { t } = useI18n();

  const downgrading = ref<boolean>(false);

  const nextBilling = computed(() => {
    return DateTime.fromISO(sessionStore.membership?.currentSubscription?.endsAt ?? "").plus({ days: 1 }).toFormat("LLL d, yyyy");
  });

  // /** NEW TIER */
  const selectedTier = ref(appStore.membershipTiers.find((tier) => tier.id === subscriptionFlowStore.membershipTierId)!);
  const selectedTierPlan = computed(() => membershipTierPlans.value.find((plan) => plan.id === subscriptionFlowStore.membershipTierPlanId)!);

  const membershipTierPlans = computed(() => {
    return selectedTier.value.membershipTierPlans.sort((a, b) => a.price - b.price);
  });

  async function handleConfirmDowngrade() {
    if (!selectedTier.value) {
      return;
    }

    downgrading.value = true;
    subscriptionFlowStore.canClose = false;

    const dto = <UpdateMembershipPayload>({
      membershipTierId: selectedTier.value.id,
      membershipTierPlanId: selectedTierPlan.value.id,
    });
    try {
      await MembershipApiService.update(dto);
      await sessionStore.refreshUser();

      downgrading.value = false;
      subscriptionFlowStore.canClose = true;

      // Close the modal at the end of the upgrade
      subscriptionFlowStore.close();
      useToast(t('subscription.flow.tier.downgrade'), ToastType.Success);

    } catch (e) {
      console.log(e);
      downgrading.value = false;
      useToast((e as ApiError).message, ToastType.Error);
    }
    subscriptionFlowStore.close();
  }
</script>

<template>
  <div class="review-modal">
    <div class="title-area t-title-2 t-bold">
      {{ $t('modal.downgrade.review.changes') }}
    </div>
    <div class="current-plan-card mt-5">
      <div class="change-body">
        <span class="name">
          {{ $t('modal.downgrade.current.plan') }}:
        </span>
        <span class="tier-info">
          {{ sessionStore.currentTier?.name }}
          <v-chip background-color="neutral-200" size="small">
            <span class="uppercase">
              {{ sessionStore.currentTierPlan?.frequency == MembershipTierPlanFrequency.YEAR
                  ? $t('common.annual')
                  : $t('common.monthly')
              }}
            </span>
          </v-chip>
        </span>
      </div>
      <div class="price">
        ${{ sessionStore.currentTierPlan?.price ?? null }}{{ $t(`common.frequency.short.${sessionStore.currentTierPlan?.frequency}`) }}
      </div>
    </div>
    <div class="new-plan-card mt-5">
      <div class="change-body">
        <span class="name">
          {{ $t('modal.downgrade.new.plan') }}:
        </span>
        <span class="tier-info">
          {{ selectedTier.name }}
          <v-chip background-color="neutral-200" size="small">
            <span class="uppercase">
              {{
                selectedTierPlan?.frequency == MembershipTierPlanFrequency.YEAR
                  ? $t('common.annual')
                  : $t('common.monthly')
              }}
            </span>
          </v-chip>
        </span>
      </div>
      <div class="price">
        ${{ selectedTierPlan.price }}{{ $t(`common.frequency.short.${selectedTierPlan?.frequency}`) }}
      </div>
    </div>
    <span class="change-notify mt-3">{{ $t('modal.downgrade.effect') }} {{ nextBilling }}</span>
    <div class="actions mt-5">
      <v-btn class="full" :loading="downgrading" @click="handleConfirmDowngrade">
        {{ $t('modal.downgrade.confirm.change') }}
      </v-btn>
      <v-btn class="full" variant="outlined" @click="subscriptionFlowStore.close();">
        {{ $t('modal.downgrade.cancel') }}
      </v-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .review-modal {
    .actions {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 1.44rem;
    }

    .full {
      width: 100%;
      border-radius: 0.5rem;
    }
  }

  .title-area {
    gap: 1.44rem;
  }

  .current-plan-card {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 1.5rem;
    border-radius: 0.5rem;
    gap: 1.44rem;
    background: rgb(var(--v-theme-neutral-100));
  }

  .new-plan-card {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 1.5rem;
    border-radius: 0.5rem;
    gap: 1.44rem;
    border: 0.0625rem solid  rgb(var(--v-theme-neutral-300));
    background: #FFFFFF;
  }

  .uppercase::first-letter {
    text-transform: uppercase;
  }

  .tier-info {
    font-size: getFontSize('subtitle-2');
    font-weight: getFontWeight('bold');
  }

  .change-body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .name {
    font-size: getFontSize('caption');
    font-weight: getFontWeight('medium');
  }

  .price {
    display: flex;
    align-items: flex-end;
    font-size: getFontSize('body-1');
    font-weight: getFontWeight('medium');
  }

  .change-notify {
    display: flex;
    justify-content: center;
    color: rgb(var(--v-theme-neutral-600));
    gap: 1.44rem;
  }
</style>